import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { fetchCategory, fetchFacts, recordTestAnswer, recordTestSession, reportFactError, reportFactLogicIssue } from "@/lib/api";
import {
  recordAnswer,
  finalizeTest,
  resetTestProgress,
  getTestProgress,
} from "@/lib/testProgress";
import Header from "@/components/Header";
import KeyHint from "@/components/KeyHint";
import {
  Check,
  X,
  Home as HomeIcon,
  RefreshCcw,
  ArrowLeft,
  Trophy,
  Flag,
  RotateCcw,
  Clock3,
} from "lucide-react";

function shuffleArray(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const REVEAL_MS = 1100;
const TEST_QUESTION_LIMIT = 100;

function pickQuestionIds(pool) {
  return shuffleArray(pool.map((f) => f.id)).slice(0, TEST_QUESTION_LIMIT);
}

function getElapsedSeconds(startedAt) {
  if (!startedAt) return 0;
  return Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
}

function formatDuration(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

export default function Test() {
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();
  const isReview = searchParams.get("mode") === "review";

  const [category, setCategory] = useState(null);
  const [facts, setFacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reviewEmpty, setReviewEmpty] = useState(false);

  const [order, setOrder] = useState([]); // shuffled fact id list
  const [cursor, setCursor] = useState(0);
  const [score, setScore] = useState({ correct: 0, wrong: 0 });
  const [revealed, setRevealed] = useState(null);
  const [finished, setFinished] = useState(false);
  const [endedEarly, setEndedEarly] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [reportedFactIds, setReportedFactIds] = useState([]);
  const [logicReportedFactIds, setLogicReportedFactIds] = useState([]);
  const [reportNotice, setReportNotice] = useState("");
  const [testStartedAt, setTestStartedAt] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [pendingAnswers, setPendingAnswers] = useState([]);
  const [earlyFinishNotice, setEarlyFinishNotice] = useState("");

  const saveTestSession = useCallback(
    ({ correct, wrong, answered, duration, endedEarly }) => {
      const questionCount = order.length;
      const denom = endedEarly ? Math.max(answered, 1) : Math.max(questionCount, 1);
      const scorePct = answered > 0 ? Math.round((correct / denom) * 100) : 0;
      recordTestSession({
        category_id: categoryId,
        mode: isReview ? "review" : "test",
        question_count: questionCount,
        answered_count: answered,
        correct_count: correct,
        wrong_count: wrong,
        score_pct: scorePct,
        duration_seconds: duration,
        ended_early: endedEarly,
      }).catch(() => {});
    },
    [categoryId, isReview, order.length]
  );

  useEffect(() => {
    (async () => {
      try {
        const [cat, fs] = await Promise.all([
          fetchCategory(categoryId),
          fetchFacts(categoryId),
        ]);
        setCategory(cat);

        let pool = fs;
        if (isReview) {
          const prog = getTestProgress(categoryId);
          const wrongSet = new Set(prog.wrong || []);
          pool = fs.filter((f) => wrongSet.has(f.id));
          if (pool.length === 0) {
            setFacts([]);
            setOrder([]);
            setReviewEmpty(true);
            return;
          }
        }

        setFacts(pool);
        setOrder(pickQuestionIds(pool));
        setCursor(0);
        setScore({ correct: 0, wrong: 0 });
        setAnsweredCount(0);
        setPendingAnswers([]);
        setEarlyFinishNotice("");
        setReportNotice("");
        setElapsedSeconds(0);
        setTestStartedAt(Date.now());
        setEndedEarly(false);
        setFinished(false);
        setReviewEmpty(false);
      } catch (e) {
        setError("İçerik yüklenemedi.");
      } finally {
        setLoading(false);
      }
    })();
  }, [categoryId, isReview]);

  useEffect(() => {
    if (!testStartedAt || loading || reviewEmpty || finished) return undefined;
    const tick = () => setElapsedSeconds(getElapsedSeconds(testStartedAt));
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [testStartedAt, loading, reviewEmpty, finished]);

  const factsById = useMemo(() => {
    const m = {};
    facts.forEach((f) => (m[f.id] = f));
    return m;
  }, [facts]);

  const total = order.length;
  const currentFact = order[cursor] ? factsById[order[cursor]] : null;

  const answer = useCallback(
    (userSaysTrue) => {
      if (!currentFact || revealed) return;
      const actual = currentFact.is_true !== false;
      const isCorrect = userSaysTrue === actual;
      const answerRecord = { factId: currentFact.id, isCorrect };
      recordAnswer(categoryId, currentFact.id, isCorrect);
      setPendingAnswers((answers) => [...answers, answerRecord]);
      let nextCorrect = 0;
      let nextWrong = 0;
      setScore((s) => {
        nextCorrect = s.correct + (isCorrect ? 1 : 0);
        nextWrong = s.wrong + (isCorrect ? 0 : 1);
        return { correct: nextCorrect, wrong: nextWrong };
      });
      setAnsweredCount((n) => n + 1);
      setRevealed({ factId: currentFact.id, userAnswer: userSaysTrue, actual });

      setTimeout(() => {
        setRevealed(null);
        setCursor((c) => {
          const next = c + 1;
          if (next >= order.length) {
            const pct =
              order.length > 0
                ? Math.round((nextCorrect / order.length) * 100)
                : 0;
            if (!isReview) finalizeTest(categoryId, pct);
            const duration = getElapsedSeconds(testStartedAt);
            const completedAnswers = [...pendingAnswers, answerRecord];
            setElapsedSeconds(duration);
            if (!isReview) {
              completedAnswers.forEach((item) => recordTestAnswer(item).catch(() => {}));
              saveTestSession({
                correct: nextCorrect,
                wrong: nextWrong,
                answered: order.length,
                duration,
                endedEarly: false,
              });
            }
            setFinished(true);
            return c;
          }
          return next;
        });
      }, REVEAL_MS);
    },
    [categoryId, currentFact, revealed, order.length, isReview, testStartedAt, saveTestSession, pendingAnswers]
  );

  const endTestEarly = useCallback(() => {
    if (finished) return;
    if (answeredCount === 0) {
      if (!window.confirm("Henüz hiç soru cevaplamadın. Yine de çıkmak istiyor musun?")) return;
    } else if (!window.confirm(`Testi şimdi bitirmek istediğine emin misin? ${answeredCount} sorudan değerlendirilirsin.`)) {
      return;
    }
    const duration = getElapsedSeconds(testStartedAt);
    setElapsedSeconds(duration);
    setEarlyFinishNotice(
      "Testiniz değerlendirmeye alınmadı. Testin istatistiklerde yer alması için 100 sorunun tamamını çözmeniz gerekir."
    );
    setEndedEarly(true);
    setFinished(true);
    setRevealed(null);
  }, [finished, answeredCount, testStartedAt]);

  const reportCurrentFact = useCallback(async () => {
    if (!currentFact) return;
    try {
      await reportFactError({ category, fact: currentFact });
      setReportedFactIds((ids) =>
        ids.includes(currentFact.id) ? ids : [...ids, currentFact.id]
      );
      setReportNotice("Bildirim alındı. Bu kart yönetim panelinde incelenecek.");
      window.setTimeout(() => setReportNotice(""), 2800);
    } catch {
      setReportNotice("Bildirim kaydedilemedi.");
      window.setTimeout(() => setReportNotice(""), 2800);
    }
  }, [category, currentFact]);

  const reportCurrentFactLogic = useCallback(async () => {
    if (!currentFact) return;
    try {
      await reportFactLogicIssue({ fact: currentFact });
      setLogicReportedFactIds((ids) =>
        ids.includes(currentFact.id) ? ids : [...ids, currentFact.id]
      );
      setReportNotice("Mantık bildirimi alındı. Bu önerme yönetim panelinde incelenecek.");
      window.setTimeout(() => setReportNotice(""), 2800);
    } catch {
      setReportNotice("Bildirim kaydedilemedi.");
      window.setTimeout(() => setReportNotice(""), 2800);
    }
  }, [currentFact]);

  // Keyboard shortcuts
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && ["INPUT", "TEXTAREA"].includes(e.target.tagName)) return;
      if (finished) return;
      if (e.key === "ArrowRight" || e.key === "d" || e.key === "D") {
        e.preventDefault();
        answer(true);
      } else if (e.key === "ArrowLeft" || e.key === "y" || e.key === "Y") {
        e.preventDefault();
        answer(false);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [answer, finished]);

  const restart = () => {
    if (isReview) {
      // re-shuffle the current review pool; do NOT reset stored progress
      const prog = getTestProgress(categoryId);
      const wrongSet = new Set(prog.wrong || []);
      const pool = facts.filter((f) => wrongSet.has(f.id));
      if (pool.length === 0) {
        setReviewEmpty(true);
        setFinished(false);
        return;
      }
      setFacts(pool);
      setOrder(pickQuestionIds(pool));
    } else {
      resetTestProgress(categoryId);
      setOrder(pickQuestionIds(facts));
    }
    setCursor(0);
    setScore({ correct: 0, wrong: 0 });
    setAnsweredCount(0);
    setPendingAnswers([]);
    setEarlyFinishNotice("");
    setRevealed(null);
    setReportNotice("");
    setElapsedSeconds(0);
    setTestStartedAt(Date.now());
    setFinished(false);
    setEndedEarly(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <div className="max-w-3xl mx-auto px-4 mt-24" data-testid="test-loading">
          <div className="h-[44vh] rounded-[28px] bg-white/70 border border-[#e8eae3] animate-pulse" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <div className="max-w-3xl mx-auto px-4 mt-24 text-center" data-testid="test-error">
          <p className="text-[#5c635e] mb-6">{error}</p>
          <Link to="/" className="inline-flex h-12 items-center gap-2 px-6 rounded-full bg-[#2e4733] text-[#f5f6f2]">
            <HomeIcon size={14} /> Ana sayfa
          </Link>
        </div>
      </div>
    );
  }

  // -------- Review mode but no wrong answers --------
  if (reviewEmpty) {
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <main
          className="relative z-10 max-w-3xl mx-auto px-4 pt-16 md:pt-24 text-center"
          data-testid="test-review-empty"
        >
          <div className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-[#e8eae3] text-[#2e4733] mb-8">
            <RotateCcw size={14} />
            <span className="label-eyebrow text-[#2e4733]">Tekrar listesi boş</span>
          </div>
          <div className="font-serif-fact text-[#1c2723] text-4xl md:text-5xl leading-tight tracking-tight">
            Tekrar edilecek bir şey yok
          </div>
          <p className="mt-4 text-[#5c635e] text-lg">
            Henüz yanlış cevapladığın bir cümle yok. Önce normal teste girip
            yanlışlarını biriktir, sonra burada tekrar et.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/"
              data-testid="review-empty-home"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#e8eae3] text-[#1c2723] hover:bg-white transition-colors"
            >
              <ArrowLeft size={14} /> Ana sayfa
            </Link>
            <Link
              to={`/test/${categoryId}`}
              data-testid="review-empty-start-test"
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors"
            >
              <Flag size={14} /> Teste başla
            </Link>
          </div>
        </main>
      </div>
    );
  }

  // -------- Finished screen --------
  if (finished) {
    const total = order.length;
    const denom = endedEarly ? answeredCount : total;
    const pct = denom > 0 ? Math.round((score.correct / denom) * 100) : 0;
    const skipped = endedEarly ? Math.max(0, total - answeredCount) : 0;
    const prog = getTestProgress(categoryId);
    const wrongRemaining = prog.wrong ? prog.wrong.length : 0;
    return (
      <div className="min-h-screen bg-paper bg-grain">
        <Header />
        <main
          className="relative z-10 max-w-3xl mx-auto px-4 pt-16 md:pt-24 text-center"
          data-testid="test-complete"
        >
          <div className="inline-flex items-center gap-2 px-4 h-9 rounded-full bg-[#e8eae3] text-[#2e4733] mb-8">
            {isReview ? <RotateCcw size={14} /> : <Trophy size={14} />}
            <span className="label-eyebrow text-[#2e4733]">
              {isReview
                ? endedEarly
                  ? "Tekrar erken sonlandırıldı"
                  : "Tekrar tamamlandı"
                : endedEarly
                ? "Test erken sonlandırıldı"
                : "Test tamamlandı"}
            </span>
          </div>

          {answeredCount === 0 ? (
            <>
              <div className="font-serif-fact text-[#1c2723] text-5xl md:text-6xl leading-tight tracking-tight">
                Hiç soru cevaplamadın
              </div>
              <p className="mt-4 text-[#5c635e] text-lg">
                Değerlendirme yapılamadı.
              </p>
            </>
          ) : (
            <>
              <div
                className="font-serif-fact text-[#1c2723] text-7xl md:text-[7rem] leading-none tracking-tight"
                data-testid="final-score-pct"
              >
                %{pct}
              </div>
              <p className="mt-4 text-[#5c635e] text-lg" data-testid="final-score-detail">
                <span className="text-[#4a6741] font-medium">{score.correct} doğru</span>
                <span className="mx-2">·</span>
                <span className="text-[#a55e3f] font-medium">{score.wrong} yanlış</span>
                <span className="mx-2">·</span>
                <span>{denom} soru</span>
                <span className="mx-2">·</span>
                <span>{formatDuration(elapsedSeconds)} süre</span>
                {endedEarly && skipped > 0 && (
                  <>
                    <span className="mx-2">·</span>
                    <span className="text-[#a3aba6]">{skipped} atlandı</span>
                  </>
                )}
              </p>
              <div
                className="mt-6 inline-flex items-center gap-3 rounded-2xl border border-[#e8eae3] bg-white px-5 py-3 text-[#1c2723]"
                data-testid="final-duration"
              >
                <Clock3 size={18} className="text-[#2e4733]" />
                <div className="text-left">
                  <div className="label-eyebrow text-[#a3aba6]">Çözüm süresi</div>
                  <div className="font-serif-fact text-2xl">{formatDuration(elapsedSeconds)}</div>
                </div>
              </div>
              {isReview && (
                <p
                  className="mt-3 label-eyebrow text-[#a3aba6]"
                  data-testid="review-mode-note"
                >
                  Tekrar modu — bu tur en iyi skoru etkilemez.
                  {wrongRemaining > 0
                    ? ` Listende ${wrongRemaining} yanlış kaldı.`
                    : " Tüm yanlışlarını temizledin!"}
                </p>
              )}
            </>
          )}

          {endedEarly && earlyFinishNotice && (
            <div
              className="mt-6 rounded-2xl border border-[#a55e3f]/25 bg-[#a55e3f]/10 px-5 py-4 text-[#a55e3f] text-sm leading-relaxed"
              data-testid="early-finish-notice"
            >
              {earlyFinishNotice}
            </div>
          )}

          {!isReview && prog.bestScorePct != null && prog.bestScorePct > pct && (
            <p className="mt-2 label-eyebrow text-[#a3aba6]">
              En iyi skorun: %{prog.bestScorePct}
            </p>
          )}
          {!isReview &&
            prog.bestScorePct === pct &&
            pct > 0 &&
            answeredCount > 0 && (
              <p className="mt-2 label-eyebrow text-[#4a6741]">
                Yeni en iyi skor!
              </p>
            )}

          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-3 flex-wrap">
            <Link
              to="/"
              data-testid="test-back-home"
              className="inline-flex items-center gap-2 h-12 px-6 rounded-full border border-[#e8eae3] text-[#1c2723] hover:bg-white transition-colors"
            >
              <ArrowLeft size={14} /> Ana sayfa
            </Link>
            {wrongRemaining > 0 &&
              (isReview ? (
                <button
                  type="button"
                  data-testid="test-go-review"
                  onClick={restart}
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border-2 border-[#a55e3f] text-[#a55e3f] hover:bg-[#a55e3f] hover:text-white transition-colors"
                >
                  <RotateCcw size={14} /> Yanlışlarımı tekrar dene ({wrongRemaining})
                </button>
              ) : (
                <Link
                  to={`/test/${categoryId}?mode=review`}
                  data-testid="test-go-review"
                  className="inline-flex items-center gap-2 h-12 px-6 rounded-full border-2 border-[#a55e3f] text-[#a55e3f] hover:bg-[#a55e3f] hover:text-white transition-colors"
                >
                  <RotateCcw size={14} /> Yanlışlarımı tekrar dene ({wrongRemaining})
                </Link>
              ))}
            <button
              data-testid="test-restart"
              onClick={restart}
              className="inline-flex items-center gap-2 h-12 px-7 rounded-full bg-[#2e4733] text-[#f5f6f2] hover:bg-[#1c2723] transition-colors"
            >
              <RefreshCcw size={14} />{" "}
              {isReview ? "Tekrar baştan dene" : "Tekrar test ol"}
            </button>
          </div>

          <div className="mt-16 mx-auto h-px w-24 bg-[#d6d9ce]" />
          <p className="mt-6 label-eyebrow text-[#a3aba6]">
            Doğru bilgi · Yanlış bilgi · Sen karar ver
          </p>
        </main>
      </div>
    );
  }

  // -------- Active test --------
  const pct = total > 0 ? Math.round((cursor / total) * 100) : 0;
  const revealCorrect = revealed && revealed.userAnswer === revealed.actual;

  return (
    <div className="min-h-screen bg-paper bg-grain pb-20">
      <Header
        right={
          <button
            data-testid="test-finish-early"
            onClick={endTestEarly}
            className="inline-flex items-center gap-2 h-9 px-4 rounded-full bg-[#e8eae3] text-[#1c2723] hover:bg-[#d6d9ce] transition-colors text-sm font-medium"
            title="Şu ana kadar cevapladığın sorulara göre değerlendir"
          >
            <Flag size={13} strokeWidth={2.2} />
            <span className="hidden sm:inline">Testi bitir</span>
            <span className="sm:hidden">Bitir</span>
          </button>
        }
      />

      <main className="relative z-10 max-w-5xl mx-auto px-4 md:px-2 pt-10 md:pt-14">
        {/* Progress + score header */}
        <div className="max-w-3xl mx-auto mb-10">
          <div className="flex items-center justify-between mb-2">
            <span className="label-eyebrow" data-testid="test-stat-progress">
              Soru {Math.min(cursor + 1, total)} / {total}
            </span>
            <span className="label-eyebrow text-[#a3aba6] inline-flex items-center gap-2">
              <span className="text-[#4a6741]" data-testid="test-stat-correct">
                {score.correct} doğru
              </span>
              <span>·</span>
              <span className="text-[#a55e3f]" data-testid="test-stat-wrong">
                {score.wrong} yanlış
              </span>
              <span>·</span>
              <span className="inline-flex items-center gap-1" data-testid="test-timer">
                <Clock3 size={12} />
                {formatDuration(elapsedSeconds)}
              </span>
            </span>
          </div>
          <div className="relative h-[3px] w-full bg-[#e8eae3] rounded-full overflow-hidden">
            <div
              className="absolute inset-y-0 left-0 bg-[#4a6741] rounded-full transition-[width] duration-500"
              style={{ width: `${pct}%` }}
            />
          </div>
        </div>

        {/* Category hint */}
        <div className="text-center mb-6 md:mb-8">
          <span className="label-eyebrow" data-testid="test-mode-label">
            {isReview ? "Tekrar göster" : "Test"} · {category?.name} · {total} soru
          </span>
        </div>

        {/* Card */}
        <div className="relative w-full max-w-4xl mx-auto" data-testid="test-card-wrap">
          <AnimatePresence mode="wait">
            <motion.article
              key={currentFact?.id || "empty"}
              initial={{ opacity: 0, y: 24, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 1.02 }}
              transition={{ type: "spring", damping: 24, stiffness: 220 }}
              className={`bg-[#ffffff] fact-card-shadow border rounded-[28px] p-8 md:p-14 lg:p-16 min-h-[44vh] flex flex-col justify-between transition-colors duration-300 ${
                revealed
                  ? revealCorrect
                    ? "border-[#4a6741]"
                    : "border-[#a55e3f]"
                  : "border-[#e8eae3]"
              }`}
              data-testid="test-card"
            >
              <div className="flex items-center justify-between">
                <span className="label-eyebrow">Doğru mu, yanlış mı?</span>
                <div className="h-px flex-1 mx-6 bg-[#e8eae3]" />
                <span className="font-mono-key text-[10px] tracking-widest text-[#a3aba6]">
                  {String(cursor + 1).padStart(3, "0")} / {String(total).padStart(3, "0")}
                </span>
              </div>

              <p
                className="font-serif-fact text-[#1c2723] text-2xl md:text-3xl lg:text-[2.45rem] leading-[1.35] my-10 md:my-14 text-left"
                data-testid="test-fact-text"
              >
                {currentFact?.text}
              </p>

              <div className="min-h-[28px] flex items-center justify-between">
                {revealed ? (
                  <motion.div
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    className={`flex items-center gap-2 text-sm font-medium ${
                      revealCorrect ? "text-[#4a6741]" : "text-[#a55e3f]"
                    }`}
                    data-testid="test-reveal"
                  >
                    {revealCorrect ? (
                      <>
                        <Check size={16} strokeWidth={2.4} />
                        <span>Bildin! Cümle {revealed.actual ? "doğru" : "yanlış"}.</span>
                      </>
                    ) : (
                      <>
                        <X size={16} strokeWidth={2.4} />
                        <span>
                          Olmadı — cümle aslında {revealed.actual ? "doğru" : "yanlış"}.
                        </span>
                      </>
                    )}
                  </motion.div>
                ) : (
                  <div className="label-eyebrow text-[#a3aba6]">
                    Sakince oku · karar ver
                  </div>
                )}
                <div className="hidden md:flex items-center gap-3 label-eyebrow text-[#a3aba6]">
                  <span className="flex items-center gap-1">
                    <KeyHint keyLabel="Y" /> yanlış
                  </span>
                  <span className="flex items-center gap-1">
                    <KeyHint keyLabel="D" /> doğru
                  </span>
                </div>
              </div>
            </motion.article>
          </AnimatePresence>
        </div>

        {/* Actions */}
        <div className="mt-10 grid grid-cols-2 gap-3 sm:gap-5 max-w-2xl mx-auto">
          <button
            data-testid="btn-wrong"
            onClick={() => answer(false)}
            disabled={!currentFact || !!revealed}
            className="group inline-flex items-center justify-center gap-3 h-16 px-6 rounded-2xl border-2 border-[#a55e3f]/30 bg-white text-[#a55e3f] hover:bg-[#a55e3f] hover:text-white hover:border-[#a55e3f] active:scale-[0.98] transition-all duration-200 disabled:opacity-40"
          >
            <X size={20} strokeWidth={2.4} />
            <span className="font-medium text-lg">Yanlış</span>
          </button>

          <button
            data-testid="btn-correct"
            onClick={() => answer(true)}
            disabled={!currentFact || !!revealed}
            className="group inline-flex items-center justify-center gap-3 h-16 px-6 rounded-2xl border-2 border-[#4a6741]/30 bg-white text-[#4a6741] hover:bg-[#2e4733] hover:text-white hover:border-[#2e4733] active:scale-[0.98] transition-all duration-200 disabled:opacity-40"
          >
            <Check size={20} strokeWidth={2.4} />
            <span className="font-medium text-lg">Doğru</span>
          </button>
        </div>

        <div className="mt-4 flex flex-col items-center gap-2">
          <button
            data-testid="btn-report-error"
            onClick={reportCurrentFact}
            disabled={!currentFact}
            className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full border border-[#d6d9ce] bg-white text-[#5c635e] hover:text-[#a55e3f] hover:border-[#a55e3f]/40 transition-colors text-sm disabled:opacity-40"
            title="Bu kartın doğru/yanlış işaretinde hata olduğunu düşünüyorsan bildir"
          >
            <Flag size={13} strokeWidth={2.2} />
            {reportedFactIds.includes(currentFact?.id) ? "Bildirildi" : "Hata bildir"}
          </button>
          <button
            data-testid="btn-report-logic"
            onClick={reportCurrentFactLogic}
            disabled={!currentFact}
            className="inline-flex items-center justify-center gap-2 h-10 px-5 rounded-full border border-[#d6d9ce] bg-white text-[#5c635e] hover:text-[#2e4733] hover:border-[#2e4733]/40 transition-colors text-sm disabled:opacity-40"
            title="Önermenin kendi içinde mantık sorunu olduğunu düşünüyorsan bildir"
          >
            <Flag size={13} strokeWidth={2.2} />
            {logicReportedFactIds.includes(currentFact?.id) ? "Mantık bildirildi" : "Önerme mantıksız"}
          </button>
          {reportNotice && (
            <div
              className="text-xs text-[#4a6741]"
              data-testid="report-notice"
            >
              {reportNotice}
            </div>
          )}
        </div>

        <div className="mt-8 text-center label-eyebrow text-[#a3aba6]">
          Klavye: <KeyHint keyLabel="Y" /> yanlış · <KeyHint keyLabel="D" /> doğru · ← / →
        </div>
      </main>
    </div>
  );
}
